import { useState } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { StateType } from '../../../states';
import { AnyObject } from '../../../types/utils';
import { selectMissionComplexeStatusComputationProps } from '../components/MissionCard/MissionCard.selector';
import { MissionCardComplexeStatuses } from '../types/components';
import { type MissionCardComplexeStatus } from '../types/components';
import { UserMissionStatus, UserMissionStatuses } from '../types/state/userMissions';

type GetMissionStatusType = {
  endDate?: number;
  status?: UserMissionStatus;
  hasUserRegistered: boolean;
  optInRequired: boolean;
  nbAchievementsPendingBets: number;
};

const getMissionStatus = ({
  endDate,
  status,
  hasUserRegistered,
  optInRequired,
  nbAchievementsPendingBets,
}: GetMissionStatusType): MissionCardComplexeStatus => {
  const hasMissionExpired = Date.now() > (endDate ?? Infinity);

  if (status === UserMissionStatuses.WON) {
    return MissionCardComplexeStatuses.WON;
  }
  if (hasMissionExpired) {
    if (nbAchievementsPendingBets) {
      return MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS;
    }
    return MissionCardComplexeStatuses.EXPIRED;
  }
  if (!hasUserRegistered && optInRequired) {
    return MissionCardComplexeStatuses.OPEN;
  }
  return MissionCardComplexeStatuses.RUNNING;
};

const useMissionComplexeStatus = (
  { missionId }: AnyObject,
  fakeState?: StateType,
): MissionCardComplexeStatus | null => {
  const selectorProps = useMemo(() => ({ missionId }), [missionId]);
  const computationData = useSelector((state: StateType) =>
    selectMissionComplexeStatusComputationProps(fakeState ?? state, selectorProps),
  );

  const initMissionStatus = getMissionStatus(computationData);
  const [missionStatus, setMissionStatus] = useState(initMissionStatus);

  const computeStatus = useCallback(() => {
    setMissionStatus(getMissionStatus(computationData));
  }, [computationData]);

  useEffect(() => {
    computeStatus();
  }, [computeStatus]);

  const intervalPid = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (intervalPid.current) {
      clearInterval(intervalPid.current);
      intervalPid.current = null;
    }
    intervalPid.current = setInterval(() => {
      computeStatus();
    }, 1000);
  }, [computeStatus]);

  useEffect(() => {
    return () => {
      if (intervalPid.current) {
        clearInterval(intervalPid.current);
        intervalPid.current = null;
      }
    };
  }, []);

  return missionStatus;
};

export default useMissionComplexeStatus;
